import { useState, useCallback, useMemo } from 'react'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  FormContainer,
  FormItemContainer,
  GreenContainer,
  GreenContainerQuestionWrapper,
  OptionButtonsContainer,
} from '../../../components/application/defaultComponents'
import { FlexDiv, FormInputErrorMessage } from 'src/components/application/formComponents/styles'
import PrebuiltRenewalFooter from '../../../components/application/PrebuiltRenewalFooter'
import { useRenewal } from '../../../context/renewal'
import StepBullet from 'src/components/basic/StepBullet'
import { RealEstateInfoData } from 'src/dtos/applicationData'
import OptionButton from '../../../components/basic/OptionButton'
import { ValidationError } from 'yup'
import StandardInput from '../../../components/basic/StandardInput'
import OptionFile from '../../../components/application/formComponents/OptionFile'
import FileData from 'src/dtos/fileData'
import { LeftBar } from 'src/components/basic/LeftBar'
import { FirmRealEstateSchema } from 'src/yup/RenewalSchema'
import { ValidationErrorMessage } from 'src/components/basic/ValidationErrorMessage/ValidationErrorMessage'

const RenewalRealEstate = () => {
  const { renewalData, saveRenewalData } = useRenewal()
  const [realEstateInfo, setRealEstateInfo] = useState<RealEstateInfoData>(
    () => !!renewalData.firmSection?.realEstateInfo ? { ...renewalData.firmSection.realEstateInfo } : {}
  )

  const [errorStates, setErrorStates] = useState(false)

  const updatedApplicationData = () => (
    {
      ...renewalData,
      currentPage: window.location.pathname,
      firmSection:
      {
        ...renewalData.firmSection,
        realEstateInfo
      }
    }
  )

  const goNextRoute = () => {
    const sec1 = renewalData.firmSection?.practiceAreas?.find(a => a.area === 'Securities Municipal Bonds - Tax Exempt')
    const sec2 = renewalData.firmSection?.practiceAreas?.find(a => a.area === 'Securities - Other Types Of Securities Practice')

    if((sec1 || sec2) ){
      return '/customer/renew/firm/securities-law'
    }

    return '/customer/renew/attorneys/left'
  }

  const goBackRoute = () => {
    return '/customer/renew/firm/percentages'
  }

  const validatePage = useCallback(() => {
    try {
      FirmRealEstateSchema.validateSync(realEstateInfo, { abortEarly: false })
      return []
    } catch (err) {
      const { errors } = err as ValidationError
      return errors
    }
  }, [realEstateInfo, FirmRealEstateSchema])

  const pageValidation = useMemo(() => {
    const incorrectSections = validatePage()

    let steps: { [key: string]: boolean } = {}

    for (let stepNumber = 1; stepNumber <= 5; stepNumber++) {
      steps[`step${stepNumber}`] = !incorrectSections.includes(`step${stepNumber}`)
    }

    return steps
  }, [validatePage])

  const handleScrollToFirstErrorState = () => {
    const incorrectSections = validatePage()
    if (incorrectSections.length > 0) {
      document.getElementById(incorrectSections[0])?.scrollIntoView({ behavior: 'smooth' })
      setErrorStates(true)
    }
  }

  const handleUploadFile = async (fileData: FileData[] | undefined, prop: 'agreementWithTitleCompaniesFile') => {
    if(!fileData) {
      setRealEstateInfo(prev => ({ ...prev, [prop]: undefined }))
      try {
        await saveRenewalData({
          ...renewalData,
          stoppedAt: '/customer/renew/firm/real-estate',
          firmSection: { ...renewalData.firmSection, realEstateInfo: { ...realEstateInfo, [prop]: undefined } }
        })
      } catch (_) { }
    } else {
      const updatedFiles = [...(realEstateInfo?.[prop] ?? []), ...fileData]
      setRealEstateInfo(prev => ({ ...prev, [prop]: updatedFiles }))
      try {
        await saveRenewalData({
          ...renewalData,
          stoppedAt: '/customer/renew/firm/real-estate',
          firmSection: { ...renewalData.firmSection, realEstateInfo: { ...realEstateInfo, [prop]: updatedFiles } }
        })
      } catch (_) { }
    }
  }

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent width='80%'>
        <DefaultTitle>Please fill out the following regarding your Real Estate practice.</DefaultTitle>
        <GreenContainer>
          <div className='green-container-content'>
            <h6>You’re being asked to fill out this supplemental real estate information because your firm has areas of practice in Real Estate.</h6>
            <span>If this information is incorrect, please change your answers.</span>
            <div>
              <LeftBar />
              <GreenContainerQuestionWrapper>
                <div className='green-container-question-div'>
                  <StepBullet>2</StepBullet>
                  <FlexDiv dir='column' gap="24px">
                    <p>
                      Real Estate
                    </p>
                    {
                      renewalData.firmSection?.practiceAreas?.find((x) => x.area === 'Real Estate')?.types?.map((pt, index) =>
                        <p key={index}>
                          {pt.percentage}%&nbsp;{pt.type}
                        </p>
                      )
                    }
                  </FlexDiv>
                </div>
              </GreenContainerQuestionWrapper>
            </div>
          </div>
        </GreenContainer>
        <FormContainer maxWidth='760px' style={{ marginTop: '90px' }}>
          <FormItemContainer maxWidth='100%'>
            <StepBullet id="step1" isComplete={pageValidation.step1}>1</StepBullet>
            <OptionButtonsContainer labelSize='big'>
              <span>Is any firm member a licensed escrow officer for a title company?</span>
              <div>
                <OptionButton
                  checked={realEstateInfo?.hasFirmMemberWithEscrowLicense === true}
                  onChange={() => setRealEstateInfo({ ...realEstateInfo, hasFirmMemberWithEscrowLicense: true })}
                  isIncorrect={!pageValidation.step1 && errorStates}
                  errorCode='EC23'
                >
                  Yes
                </OptionButton>
                <OptionButton
                  checked={realEstateInfo?.hasFirmMemberWithEscrowLicense === false}
                  onChange={() => setRealEstateInfo({
                    ...realEstateInfo,
                    hasFirmMemberWithEscrowLicense: false
                  })}
                  isIncorrect={!pageValidation.step1 && errorStates}
                  errorCode='EC23'
                >
                  No
                </OptionButton>
              </div>
              <ValidationErrorMessage errorCode='EC23' />
              {
                !pageValidation.step1 && errorStates &&
                <FormInputErrorMessage>
                  This is a required field
                </FormInputErrorMessage>
              }
            </OptionButtonsContainer>
          </FormItemContainer>
          <FormItemContainer maxWidth='100%'>
            <StepBullet id="step2" isComplete={pageValidation.step2}>2</StepBullet>
            <OptionButtonsContainer labelSize='big'>
              <span>Does Applicant or any firm member have any agreements with title companies?</span>
              <OptionFile
                title=''
                fileHint='If not previously reported, please provide any new title agreement, fee attorney agreement or P22 agreement with any title companies'
                showOptions={true}
                filesData={realEstateInfo?.agreementWithTitleCompaniesFile ?? undefined}
                setFilesData={fileData => handleUploadFile(fileData, 'agreementWithTitleCompaniesFile')}
                selectedOption={realEstateInfo?.hasAgreementWithTitleCompanies}
                setSelectedOption={option => setRealEstateInfo(prev => ({ ...prev, hasAgreementWithTitleCompanies: option }))}
                isIncorrect={!pageValidation.step2 && errorStates}
                fontSize='small'
                errorCode='EC24'
              />
              {
                !pageValidation.step2 && errorStates &&
                <FormInputErrorMessage>
                  This is a required field
                </FormInputErrorMessage>
              }
            </OptionButtonsContainer>
          </FormItemContainer>
          <FormItemContainer maxWidth='100%'>
            <StepBullet id="step3" isComplete={pageValidation.step3}>3</StepBullet>
            <OptionButtonsContainer labelSize='big'>
              <span>Is any firm member a fee attorney for a title company?</span>
              <div>
                <OptionButton
                  checked={realEstateInfo?.hasFirmMemberFeeAttorney === true}
                  onChange={() => setRealEstateInfo({ ...realEstateInfo, hasFirmMemberFeeAttorney: true })}
                  isIncorrect={!pageValidation.step3 && errorStates}
                  errorCode='EC26'
                >
                  Yes
                </OptionButton>
                <OptionButton
                  checked={realEstateInfo?.hasFirmMemberFeeAttorney === false}
                  onChange={() => setRealEstateInfo({ ...realEstateInfo, hasFirmMemberFeeAttorney: false })}
                  isIncorrect={!pageValidation.step3 && errorStates}
                  errorCode='EC26'
                >
                  No
                </OptionButton>
              </div>
                <ValidationErrorMessage errorCode='EC26' />
              {
                !pageValidation.step3 && errorStates &&
                <FormInputErrorMessage>
                  This is a required field
                </FormInputErrorMessage>
              }
            </OptionButtonsContainer>
          </FormItemContainer>
          <FormItemContainer maxWidth='100%'>
            <StepBullet id="step4" isComplete={pageValidation.step4}>4</StepBullet>
            <OptionButtonsContainer labelSize='big'>
              <span>Is any firm member an outside closing attorney, approved attorney or P-22 attorney?</span>
              <div>
                <OptionButton
                  checked={realEstateInfo?.hasFirmMemberOutsideClosingAttorney === true}
                  onChange={() => setRealEstateInfo({ ...realEstateInfo, hasFirmMemberOutsideClosingAttorney: true })}
                  isIncorrect={!pageValidation.step4 && errorStates}
                  errorCode='EC27'
                >
                  Yes
                </OptionButton>
                <OptionButton
                  checked={realEstateInfo?.hasFirmMemberOutsideClosingAttorney === false}
                  onChange={() => setRealEstateInfo({ ...realEstateInfo, hasFirmMemberOutsideClosingAttorney: false })}
                  isIncorrect={!pageValidation.step4 && errorStates}
                  errorCode='EC27'
                >
                  No
                </OptionButton>
              </div>
              <ValidationErrorMessage errorCode='EC27' />
              {
                !pageValidation.step4 && errorStates &&
                <FormInputErrorMessage>
                  This is a required field
                </FormInputErrorMessage>
              }
            </OptionButtonsContainer>
          </FormItemContainer>
          <FormItemContainer maxWidth='100%'>
            <StepBullet id="step5" isComplete={pageValidation.step5}>5</StepBullet>
            <StandardInput
              label="How many non-attorney licensed escrow officers are currently employed by your firm?"
              type="number"
              labelSize='high'
              value={realEstateInfo.nonAttorneysWithEscrowLicenseQuantity ?? ''}
              onChange={({ target: { value } }) => setRealEstateInfo(prev => ({ ...prev, nonAttorneysWithEscrowLicenseQuantity: (isNaN(Number(value)) || !value) ? undefined : Number(value) }))}
              width='25%'
              isIncorrect={!pageValidation.step5 && errorStates}
              errorMessage={!pageValidation.step5 && errorStates ? 'This is a required field' : ''}
              errorCode='EC28'
            />
          </FormItemContainer>
        </FormContainer>
      </ApplicationBodyContent>
      <PrebuiltRenewalFooter
        goBackRoute={goBackRoute()}
        isFormValid={Object.keys(pageValidation).every(key => pageValidation[key] === true)}
        nextRoute={goNextRoute()}
        updatedApplication={updatedApplicationData()}
        beforeSaveContinueClick={handleScrollToFirstErrorState}
      />
    </ApplicationBodyContainer>
  )
}

export default RenewalRealEstate